
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Calendar, Trophy, Settings, Plus, Edit, UserPlus, BarChart3 } from 'lucide-react';
import { EnhancedGameCreationModal } from '@/components/games/EnhancedGameCreationModal';
import { useTeams } from '@/hooks/useTeams';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { HistoricalPerformance } from '@/components/retention/HistoricalPerformance';
import { SeasonArchiveManager } from '@/components/retention/SeasonArchiveManager';
import { EntityRolloverScheduler } from '@/components/seasons/EntityRolloverScheduler';
import { NotificationAnalyticsChart } from '@/components/notifications/NotificationAnalyticsChart';
import { HistoricalPerformanceComparison } from '@/components/analytics/HistoricalPerformanceComparison';
import { RevenueAnalyticsDashboard } from '@/components/analytics/RevenueAnalyticsDashboard';
import { QuickActionCards } from '@/components/dashboard/QuickActionCards';
import PermissionGate from '@/components/rbac/PermissionGate';
import { CreateTeamModal } from '@/components/teams/CreateTeamModal';
import EditTeamModal from '@/components/teams/EditTeamModal';
import PlayerManagementModal from '@/components/teams/PlayerManagementModal';
import { coachServiceFixed as coachService } from '@/services/coachServiceFixed';
import { teamService } from '@/services/teamService';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const CoachDashboard: React.FC = () => {
  const { teams, loading, loadTeams } = useTeams();
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [editingTeam, setEditingTeam] = React.useState<any>(null);
  const [showPlayerModal, setShowPlayerModal] = React.useState(false);
  const [selectedTeamForPlayers, setSelectedTeamForPlayers] = React.useState<any>(null);
  const [coachStats, setCoachStats] = React.useState({
    totalTeams: 0,
    totalPlayers: 0,
    upcomingGames: 0,
    activeConnections: 0
  });
  const [teamPlayers, setTeamPlayers] = React.useState<any[]>([]);
  const [coachTeamIds, setCoachTeamIds] = React.useState<string[]>([]);
  const [showGameModal, setShowGameModal] = React.useState(false);
  const { toast: toastHook } = useToast();

  const currentUserId = profile?.id || internalUser?.id;
  const userTeams = teams.filter(team => team.created_by === currentUserId);

  const handleViewTeam = (teamSlug: string) => {
    navigate(`/team/${teamSlug}`);
  };

  const handleManagePlayers = async (team: any) => {
    setSelectedTeamForPlayers(team);
    try {
      const players = await teamService.getTeamPlayers(team.team_id);
      setTeamPlayers(players);
      setShowPlayerModal(true);
    } catch (error) {
      console.error('Error loading team players:', error);
      toast.error('Failed to load team players');
    }
  };

  const handlePlayersUpdate = async () => {
    if (selectedTeamForPlayers) {
      try {
        const players = await teamService.getTeamPlayers(selectedTeamForPlayers.team_id);
        setTeamPlayers(players);
        loadCoachStats();
      } catch (error) {
        console.error('Error refreshing players:', error);
      }
    }
  };

  const loadCoachStats = React.useCallback(async () => {
    if (currentUserId) {
      try {
        // Get teams managed by this coach
        const { data: coachTeams } = await supabase
          .from('teams')
          .select('team_id')
          .eq('created_by', currentUserId);

        const teamIds = coachTeams?.map(t => t.team_id) || [];
        const totalTeams = teamIds.length;

        // Get total players across all teams
        let totalPlayers = 0;
        if (teamIds.length > 0) {
          const { count: playerCount } = await supabase
            .from('team_players')
            .select('*', { count: 'exact', head: true })
            .in('team_id', teamIds);
          totalPlayers = playerCount || 0;
        }

        // Get upcoming games for these teams
        let upcomingGames = 0;
        if (teamIds.length > 0) {
          const today = new Date().toISOString().split('T')[0];
          const { count: gameCount } = await supabase
            .from('games')
            .select('*', { count: 'exact', head: true })
            .or(`home_team_id.in.(${teamIds.join(',')}),away_team_id.in.(${teamIds.join(',')})`)
            .gte('game_date', today)
            .not('game_status', 'in', '(completed,forfeited,cancelled)');
          upcomingGames = gameCount || 0;
        }

        setCoachStats({ 
          totalTeams, 
          totalPlayers, 
          upcomingGames, 
          activeConnections: 0 
        });
      } catch (error) {
        console.error('Error loading coach stats:', error);
      }
    }
  }, [currentUserId]);

  const loadCoachTeamIds = async () => {
    if (!profile?.id) return;
    
    const { data } = await supabase
      .from('teams')
      .select('team_id')
      .eq('created_by', profile.id);
    
    if (data) {
      setCoachTeamIds(data.map(t => t.team_id.toString()));
    }
  };

  React.useEffect(() => {
    loadCoachStats();
    loadCoachTeamIds();
  }, [loadCoachStats, teams]);

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <PermissionGate permission="manage_team">
        <QuickActionCards 
          userRole="coach"
          onCreateTeam={() => setShowCreateModal(true)}
          onManagePlayers={() => navigate('/manage-players')}
          onScheduleGame={() => setShowGameModal(true)}
          onPostLesson={() => navigate('/training-sessions/create')}
        />
      </PermissionGate>

      {/* Team Management Section */}
      <PermissionGate permission="manage_team">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-sports-green flex items-center gap-2">
                <Users className="h-6 w-6" />
                My Teams
              </CardTitle>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Team
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-gray-400">Loading teams...</div>
            ) : userTeams.length > 0 ? (
              <div className="space-y-4">
                {userTeams.map((team) => (
                  <div key={team.team_id} className="bg-gray-700 p-4 rounded-lg">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1">
                        {team.team_logo && (
                          <img 
                            src={team.team_logo} 
                            alt={team.team_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h3 className="text-white font-semibold">{team.team_name}</h3>
                          <p className="text-gray-400 text-sm">{team.sport_type || 'Sport not specified'}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleManagePlayers(team)}
                          className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Players
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingTeam(team)}
                          className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewTeam(team.slug)}
                          className="border-gray-500 text-gray-300 hover:bg-gray-600"
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">You haven't created any teams yet.</p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  Create Your First Team
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </PermissionGate>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <Trophy className="h-8 w-8 text-sports-green" />
             <div>
               <h4 className="font-semibold text-white">Teams Managed</h4>
               <p className="text-sm text-gray-300">{coachStats.totalTeams}</p>
             </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <Users className="h-8 w-8 text-sports-green" />
             <div>
               <h4 className="font-semibold text-white">Total Members</h4>
               <p className="text-sm text-gray-300">{coachStats.totalPlayers}</p>
             </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4 flex items-center space-x-4">
            <Calendar className="h-8 w-8 text-sports-green" />
             <div>
               <h4 className="font-semibold text-white">Upcoming Games</h4>
               <p className="text-sm text-gray-300">{coachStats.upcomingGames}</p>
             </div>
          </CardContent>
        </Card>
      </div>

      <PermissionGate permission="manage_team">
        {/* Coaching Tools */}
        <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-xl text-sports-green flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Coaching Tools
          </CardTitle>
        </CardHeader>
        <CardContent>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="bg-gray-700 p-4 rounded-lg">
               <h5 className="font-medium text-white mb-2">Schedule Management</h5>
               <p className="text-sm text-gray-300 mb-3">Create and manage practice and game schedules</p>
               <Button 
                 variant="outline" 
                 size="sm" 
                 className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                 onClick={() => navigate('/coach-schedule')}
               >
                 <Calendar className="h-4 w-4 mr-1" />
                 View Schedule
               </Button>
             </div>
             <div className="bg-gray-700 p-4 rounded-lg">
               <h5 className="font-medium text-white mb-2">Coach Network</h5>
               <p className="text-sm text-gray-300 mb-3">Connect with other coaches and share knowledge</p>
               <Button 
                 variant="outline" 
                 size="sm" 
                 className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                 onClick={() => navigate('/coach-network')}
               >
                 <Users className="h-4 w-4 mr-1" />
                 Network ({coachStats.activeConnections})
               </Button>
             </div>
               <div className="bg-gray-700 p-4 rounded-lg">
                 <h5 className="font-medium text-white mb-2">Performance Analytics</h5>
                 <p className="text-sm text-gray-300 mb-3">Track team and player performance metrics</p>
                 <Button 
                   variant="outline" 
                   size="sm" 
                   className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                   onClick={() => navigate('/coach-analytics')}
                 >
                   <BarChart3 className="h-4 w-4 mr-1" />
                   Analytics
                 </Button>
               </div>
               <div className="bg-gray-700 p-4 rounded-lg">
                 <h5 className="font-medium text-white mb-2">Game Management</h5>
                 <p className="text-sm text-gray-300 mb-3">View game requests, codes, and manage team games</p>
                 <Button 
                   variant="outline" 
                   size="sm" 
                   className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                   onClick={() => navigate('/game-management')}
                 >
                   <Calendar className="h-4 w-4 mr-1" />
                   Manage Games
                 </Button>
               </div>
            </div>

            {/* Upcoming Games Section */}
            {coachStats.upcomingGames > 0 && (
              <div className="mt-6 bg-gray-700 p-4 rounded-lg">
                <h5 className="font-medium text-white mb-3">Upcoming Games ({coachStats.upcomingGames})</h5>
                <p className="text-sm text-gray-300 mb-3">Your teams have games scheduled</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                  onClick={() => navigate('/game-management')}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  View All Games
                </Button>
              </div>
            )}
        </CardContent>
      </Card>
      </PermissionGate>

      {/* Notification Analytics */}
      <PermissionGate permission="view_analytics">
        <NotificationAnalyticsChart />
      </PermissionGate>

      {/* Revenue Analytics */}
      <PermissionGate permission="view_analytics">
        {coachTeamIds.length > 0 && (
          <RevenueAnalyticsDashboard 
            entityId={coachTeamIds[0]}
            entityType="coach"
          />
        )}
      </PermissionGate>

      {/* Historical Performance Comparison */}
      <PermissionGate permission="view_analytics">
        {coachTeamIds.length > 0 && (
          <HistoricalPerformanceComparison 
            entityId={coachTeamIds[0]}
            entityType="coach"
          />
        )}
      </PermissionGate>

      {/* Historical Performance */}
      <PermissionGate permission="view_analytics">
        {coachTeamIds.length > 0 && (
          <HistoricalPerformance 
            entityId={coachTeamIds[0]}
            entityType="coach"
          />
        )}
      </PermissionGate>

      {/* Season Management */}
      {coachTeamIds.length > 0 && (
        <PermissionGate permission="manage_team">
          <div className="space-y-6">
            <SeasonArchiveManager 
              entityId={coachTeamIds[0]}
              entityType="coach"
            />
            <EntityRolloverScheduler 
              entityIds={coachTeamIds}
              entityType="coach"
              entityName="Coach Account"
            />
          </div>
        </PermissionGate>
      )}

      {/* Modals */}
      <CreateTeamModal 
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          loadTeams();
          loadCoachStats();
        }}
      />
      
       {editingTeam && (
         <EditTeamModal
           isOpen={!!editingTeam}
           onClose={() => setEditingTeam(null)}
           team={editingTeam}
         />
       )}

       {showPlayerModal && selectedTeamForPlayers && (
         <PlayerManagementModal
           isOpen={showPlayerModal}
           onClose={() => {
             setShowPlayerModal(false);
             setSelectedTeamForPlayers(null);
           }}
           teamId={selectedTeamForPlayers.team_id}
           teamName={selectedTeamForPlayers.team_name}
           currentPlayers={teamPlayers}
           onPlayersUpdate={handlePlayersUpdate}
         />
       )}
      <EnhancedGameCreationModal
        userId={currentUserId || ''}
        externalOpen={showGameModal}
        onExternalOpenChange={setShowGameModal}
        onGameCreated={() => setShowGameModal(false)}
      />
    </div>
  );
};

export default CoachDashboard;
